import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Download, Info, Heart, Pencil, X, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

// AI Information content component for the tooltip
const AIInfoContent = () => (
  <div className="flex flex-col space-y-3 sm:space-y-4">
    <div className="text-xs sm:text-sm">
      <p className="text-xs sm:text-sm">
        La inteligencia artificial es la tecnología de más rápida evolución en el mundo, por lo que mejora día a día.
        Sin embargo, como cualquier tecnología nueva, a veces no es perfecta. Queremos contarte de forma transparente sobre los detalles que estamos perfeccionando:
      </p>
      <ul className="list-disc pl-4 sm:pl-5 mt-1 sm:mt-2 space-y-0.5 sm:space-y-1">
        <li className="text-xs sm:text-sm">Rostros: Ocasionalmente, pueden aparecer pequeñas inconsistencias en los rasgos faciales.</li>
        <li className="text-xs sm:text-sm">Texto: El texto de tamaño reducido puede generarse de forma ilegible.</li>
        <li className="text-xs sm:text-sm">Fidelidad de la prenda: Pueden existir ligeras diferencias entre la prenda original y su representación en el video.</li>
        <li className="text-xs sm:text-sm">Audio: El audio generado puede tener pequeñas variaciones en la pronunciación.</li>
      </ul>
      <p className="mt-1 sm:mt-2 text-xs sm:text-sm">
        Nuestro equipo, junto con expertos de toda la industria, trabaja sin descanso para que la IA sea cada vez más precisa y potente.
        Gracias por ser parte de este viaje y por confiar en nosotros.
      </p>
    </div>
    <div className="flex justify-center mt-3 sm:mt-4">
      <img 
        src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Firma.png" 
        alt="Firma del equipo" 
        className="h-12 sm:h-16 object-contain" 
      />
    </div>
  </div>
);

interface GeneratedVideo {
  id: string;
  fileName: string;
  signedUrl: string;
  favorito?: boolean;
}

interface GeneratedVideoInlineProps {
  videoId: string;
  companyId: string;
  onClose?: () => void;
  onReturnToConfig?: () => void;
  onReady?: () => void;
}

const GeneratedVideoInline: React.FC<GeneratedVideoInlineProps> = ({
  videoId,
  companyId,
  onClose,
  onReturnToConfig,
  onReady,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're on the dedicated page for full-width layout
  const isStandalonePage = location.pathname === '/redes-sociales/video-generado';

  const [loading, setLoading] = useState<boolean>(true);
  const [video, setVideo] = useState<GeneratedVideo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [isFavoriting, setIsFavoriting] = useState(false);
  const [showFavoriteMessage, setShowFavoriteMessage] = useState(false);

  const hasSignaledReadyRef = useRef<boolean>(false);

  useEffect(() => {
    if (!loading && !hasSignaledReadyRef.current) {
      hasSignaledReadyRef.current = true;
      onReady?.();
    }
  }, [loading, onReady]);

  useEffect(() => {
    hasSignaledReadyRef.current = false;
  }, [videoId, companyId]);

  useEffect(() => {
    if (videoId && companyId) {
      fetchGeneratedVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, companyId]);

  const fetchGeneratedVideo = async () => {
    if (!companyId || !videoId) return;
    try {
      setError(null);
      setLoading(true);

      const maxAttempts = 10;
      const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const { data: videoData, error: videoError } = await supabase
            .from('imagenes_generadas')
            .select('file_name, favorito')
            .eq('id', videoId)
            .eq('id_company', companyId)
            .single();

          if (videoError) {
            console.warn(`Intento ${attempt}: error obteniendo video ${videoId}:`, videoError);
            await delay(2000);
            continue;
          }

          const hasFileName = videoData?.file_name !== null && videoData?.file_name !== undefined;
          if (!hasFileName) {
            await delay(2000);
            continue;
          }

          const filePath = `${companyId}/full/${videoData.file_name}`;
          const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
          if (!signedUrlData?.signedUrl) {
            await delay(2000);
            continue;
          }

          setVideo({
            id: videoId,
            fileName: videoData.file_name,
            signedUrl: signedUrlData.signedUrl,
            favorito: videoData?.favorito === true,
          });
          setLoading(false);
          return;
        } catch (innerErr) {
          console.error(`Intento ${attempt}: error inesperado para ${videoId}`, innerErr);
          await delay(2000);
        }
      }

      setError('El video aún no está listo. Por favor, inténtalo de nuevo en unos minutos.');
    } catch (err) {
      console.error('Error fetching generated video:', err);
      setError('Error al cargar el video generado. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!video) return;
    setIsDownloading(true);
    setDownloadSuccess(false);

    try {
      const response = await fetch(video.signedUrl);
 const originalBlob = await response.blob();
// Force video MIME type if storage returned incorrect Content-Type (e.g., image/png for legacy videos)
const needsMimeFix = !originalBlob.type || !originalBlob.type.startsWith('video/');
const blob = needsMimeFix ? new Blob([originalBlob], { type: 'video/mp4' }) : originalBlob;
const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      
      let fileName = video.fileName || 'video.mp4';
      // Remove ALL chained extensions like ".mp4.png" or ".webm.jpg"
      // Loop to remove extensions until none remain
      let prev = '';
      while (prev !== fileName) {
        prev = fileName;
        fileName = fileName.replace(/\.(png|jpe?g|webp|gif|bmp|tiff|svg|mp4|webm|mov)$/i, '');
      }
      fileName = fileName + '.mp4';
      a.download = fileName;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      try {
        await supabase
          .from('imagenes_generadas')
          .update({ descargada: true })
          .eq('id', video.id)
          .eq('id_company', companyId);
      } catch {}

      setDownloadSuccess(true);
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error downloading video:', err);
      setIsDownloading(false);
    }
  };

  const handleFavorite = async () => {
    if (!video) return;
    setIsFavoriting(true);
    try {
      const nextValue = !(video.favorito === true);
      const { error: updateError } = await supabase
        .from('imagenes_generadas')
        .update({ favorito: nextValue })
        .eq('id', video.id)
        .eq('id_company', companyId);
      if (updateError) {
        console.error('Error marcando como favorito:', updateError);
        alert('No se pudo marcar como favorito');
      } else {
        setVideo((prev) => prev ? { ...prev, favorito: nextValue } : prev);
        if (nextValue) {
          setShowFavoriteMessage(true);
          setTimeout(() => setShowFavoriteMessage(false), 4000);
        }
      }
    } catch (e) {
      console.error('Error marcando como favorito:', e);
      alert('No se pudo marcar como favorito');
    } finally {
      setIsFavoriting(false);
    }
  };

  const handleReturnToConfigClick = () => {
    if (onReturnToConfig) {
      onReturnToConfig();
    } else {
      navigate('/redes-sociales/crear-anuncio/seleccionar-productos');
    }
  };

  const handleCloseClick = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const handleEditVideo = () => {
    if (!video) return;
    navigate(`/redes-sociales/editar-video?id=${video.id}&companyId=${companyId}`, {
      state: {
        returnToResults: true,
        resultsVideoId: video.id,
        resultsCompanyId: companyId,
      },
    });
  };

  // Inline loading state: render nothing so parent overlay remains
  if (loading) return null;

  // Main inline view
  return (
    <div className={`pt-0 pb-0 w-full bg-white md:bg-transparent min-h-[100dvh] md:min-h-screen ${isStandalonePage ? 'md:px-4' : ''}`}>
      {/* Desktop layout */}
      <div className="hidden md:flex md:flex-row md:gap-4 md:items-start md:justify-center generated-images-desktop-height">
        {/* Video a la izquierda (desktop only) */}
        <div className="flex-shrink-0 flex items-center justify-center generated-images-desktop-height relative">
          {showFavoriteMessage && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
                <Heart className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" />
                <span className="text-base font-medium text-gray-700 whitespace-nowrap">Video marcado como favorito</span>
              </div>
            </div>
          )}
          {video ? (
            <video 
              src={video.signedUrl} 
              controls
              playsInline
              style={{ height: 'calc(100vh - 8rem)', width: 'auto', maxWidth: 'calc(100vw - 500px)' }}
              className="object-contain rounded-lg"
            >
              Tu navegador no soporta la reproducción de video.
            </video>
          ) : (
            <div className="text-gray-500">No hay video disponible</div>
          )}
        </div>

        {/* Container principal a la derecha (desktop only) */}
        <div className="flex-1 min-w-[33%] max-w-[50%] bg-white flex flex-col rounded-2xl border border-gray-200 overflow-hidden generated-images-desktop-height">
          {/* Header */}
          <div className="relative px-6 py-4 bg-white z-10 flex justify-center items-center shadow-sm rounded-t-2xl">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900 text-center">Video Generado</h2>
              <button className="inline-flex items-center justify-center cursor-pointer text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full p-1" onClick={() => setShowInfoTooltip(true)} aria-label="Mostrar información">
                <Info size={18} />
              </button>
            </div>
            {(onReturnToConfig || onClose) && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onReturnToConfig) {
                    onReturnToConfig();
                  } else if (onClose) {
                    onClose();
                  }
                }}
                className="absolute right-6 top-3 h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Listo
              </button>
            )}
          </div>

          {error && (
            <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">{error}</div>
          )}

          {/* Empty space where thumbnails would be */}
          <div className="flex-1 flex flex-col justify-between p-4">
            <div className="flex-1" />

            {/* Action buttons - vertical distribution */}
            <div className="relative">
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleFavorite}
                  disabled={isFavoriting || !video}
                  aria-pressed={video?.favorito === true}
                  className={`h-10 rounded-full font-normal transition-colors flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isFavoriting ? 'opacity-50 cursor-not-allowed' : ''} ${video?.favorito === true ? 'bg-blue-500 text-white hover:bg-blue-600 border border-transparent' : 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]'}`}
                  aria-label="Favorito"
                >
                  {isFavoriting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  ) : (
                    <Heart
                      className="w-4 h-4 flex-shrink-0"
                      fill={video?.favorito === true ? 'currentColor' : 'none'}
                    />
                  )}
                  <span className="whitespace-nowrap text-sm">Favorito</span>
                </button>
                {/* Botón de editar video oculto temporalmente
                <button onClick={handleEditVideo} disabled={!video} className={`h-10 rounded-full font-normal flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${video ? 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                  <Pencil className="w-4 h-4 flex-shrink-0" />
                  <span className="whitespace-nowrap text-sm">Editar</span>
                </button>
                */}
                <button onClick={handleDownload} disabled={!video} className={`h-10 rounded-full font-normal flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${video ? 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                  <Download className="w-4 h-4 flex-shrink-0" />
                  <span className="whitespace-nowrap text-sm">Descargar</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile layout */}
      <div className="md:hidden w-full bg-white min-h-[100dvh]">
        {/* Header */}
        <div className="relative px-4 pt-4 pb-2 bg-white z-10 flex justify-center items-center">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900 text-center">Video<br /> Generado</h2>
            <button className="inline-flex items-center justify-center cursor-pointer text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-full p-1" onClick={() => setShowInfoTooltip(true)} aria-label="Mostrar información">
              <Info size={18} />
            </button>
          </div>
          {(onReturnToConfig || onClose) && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onReturnToConfig) {
                  onReturnToConfig();
                } else if (onClose) {
                  onClose();
                }
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Listo
            </button>
          )}
        </div>

        {error && (
          <div className="mx-4 mt-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">{error}</div>
        )}

        {/* Main video */}
        {video ? (
          <div className="flex flex-col overflow-hidden pb-[calc(env(safe-area-inset-bottom,0px)+56px)] mt-3">
            <div className="flex-1 flex flex-col relative">
              {showFavoriteMessage && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
                    <Heart className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" />
                    <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Video marcado como favorito</span>
                  </div>
                </div>
              )}
              <div className="bg-white flex-1 flex items-center justify-center p-1">
                <div className="w-full flex items-center justify-center relative min-h-[200px] h-[min(calc(100dvh-160px),85dvh)] max-h-[calc(100dvh-140px)]">
                  <video 
                    src={video.signedUrl} 
                    controls
                    playsInline
                    className="max-w-full max-h-full object-contain rounded-2xl"
                  >
                    Tu navegador no soporta la reproducción de video.
                  </video>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white py-16">
            <p className="text-gray-500">No se ha generado el video</p>
          </div>
        )}

        {/* Action bar (mobile) */}
        <div className="px-4 pt-2 bg-white flex-shrink-0 z-20 fixed bottom-0 left-0 right-0" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
          <div className="relative">
            <div className="flex flex-row justify-between gap-2">
              <button
                onClick={handleFavorite}
                disabled={isFavoriting || !video}
                aria-pressed={video?.favorito === true}
                className={`w-10 h-10 rounded-full font-normal transition-colors flex items-center justify-center shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isFavoriting ? 'opacity-50 cursor-not-allowed' : ''} ${video?.favorito === true ? 'bg-blue-500 text-white hover:bg-blue-600 border border-transparent' : 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]'}`}
                aria-label="Favorito"
              >
                {isFavoriting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                ) : (
                  <Heart
                    className="w-4 h-4 flex-shrink-0"
                    fill={video?.favorito === true ? 'currentColor' : 'none'}
                  />
                )}
              </button>
              {/* Botón de editar video oculto temporalmente
              <button onClick={handleEditVideo} disabled={!video} className={`flex-1 h-10 rounded-full font-normal flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${video ? 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                <Pencil className="w-4 h-4 flex-shrink-0" />
                <span className="whitespace-nowrap text-sm">Editar</span>
              </button>
              */}
              <button onClick={handleDownload} disabled={!video} className={`flex-1 h-10 rounded-full font-normal flex items-center justify-center gap-2 px-3 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${video ? 'bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                <Download className="w-4 h-4 flex-shrink-0" />
                <span className="whitespace-nowrap text-sm">Descargar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Download Status Overlay */}
      {(isDownloading || downloadSuccess) && (
        <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4 sm:px-0">
          <div className="bg-white rounded-md shadow-lg p-6 max-w-sm w-full mx-4 sm:mx-auto flex flex-col items-center">
            {isDownloading && !downloadSuccess && (
              <>
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-4"></div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1">Preparando<br className="sm:hidden" /> descarga</h3>
              </>
            )}
            {downloadSuccess && (
              <>
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Check className="w-12 h-12 text-green-500" />
                </div>
                <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1">Video<br className="sm:hidden" /> descargado</h3>
              </>
            )}
          </div>
        </div>
      )}

      {/* Info Tooltip Modal */}
      {showInfoTooltip && (
        <div className="fixed inset-0 flex items-center justify-center z-[99999]" style={{ position: 'fixed' }}>
          <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm" onClick={() => setShowInfoTooltip(false)} />
          <div className="relative bg-white rounded-md w-[95vw] max-w-md max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-start p-4 border-b border-gray-100">
              <div className="w-8"></div>
              <h3 className="text-center font-semibold text-gray-900">Información de IA</h3>
              <button className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md p-1" onClick={() => setShowInfoTooltip(false)} aria-label="Cerrar información">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <AIInfoContent />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratedVideoInline;
